import React, { useState, useRef, useEffect } from 'react';
import { 
  X, Calendar, Users, FolderKanban, Key, FileText, 
  Link, User, Tag, Check, Code, Briefcase, LifeBuoy, Search
} from 'lucide-react';
import { Project, User as ProjectUser } from '../../types';
import { toast } from '../UI/Toast';
import { apiService } from '../../services/api';

interface ProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (projectData: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>) => Promise<any> | any;
  users: ProjectUser[];
  project?: Project;
}

export const ProjectModal: React.FC<ProjectModalProps> = ({
  isOpen,
  onClose,
  onSave,
  users,
  project
}) => {
  const [formData, setFormData] = useState({
    key: project?.key || '',
    name: project?.name || '',
    description: project?.description || '',
    type: project?.type || 'software',
    lead: project?.lead || users[0]?.id || '',
    category: project?.category || 'Development',
    url: project?.url || '',
    avatar: project?.avatar || '🚀',
    startDate: project?.startDate || '',
    endDate: project?.endDate || ''
  });

  const [step, setStep] = useState(1);
  const [createdProject, setCreatedProject] = useState<any>(null);
  const [addedMembers, setAddedMembers] = useState<any[]>([]);
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [existingUserSearch, setExistingUserSearch] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [leadSearch, setLeadSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset state on open
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setCreatedProject(null);
      setAddedMembers([]);
      setLeadSearch('');
      setExistingUserSearch('');
      setInviteEmail('');
      setFormData({
        key: project?.key || '',
        name: project?.name || '',
        description: project?.description || '',
        type: project?.type || 'software',
        lead: project?.lead || users[0]?.id || '',
        category: project?.category || 'Development',
        url: project?.url || '',
        avatar: project?.avatar || '🚀',
        startDate: project?.startDate || '',
        endDate: project?.endDate || ''
      });
    }
  }, [isOpen, project, users]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSaving) return;
    
    setIsSaving(true);
    try {
      const result = await onSave(formData);
      if (!project && result) {
        // New project creation goes to Step 2
        setCreatedProject(result);
        const leadUser = users.find(u => u.id === formData.lead) || users[0];
        setAddedMembers([{
          id: leadUser.id,
          displayName: leadUser.displayName,
          emailAddress: leadUser.emailAddress,
          avatarUrl: leadUser.avatarUrl,
          status: 'added'
        }]);
        setStep(2);
      } else {
        // Editing existing project closes the modal
        onClose();
      }
    } catch (err) {
      console.error('Failed to save project', err);
    } finally {
      setIsSaving(false);
    }
  };

  const selectedLead = users.find(u => u.id === formData.lead) || users[0];

  const filteredUsers = users.filter(user => 
    user.displayName.toLowerCase().includes(leadSearch.toLowerCase()) ||
    (user.emailAddress && user.emailAddress.toLowerCase().includes(leadSearch.toLowerCase()))
  );

  // Step 2 Invitation variables & helpers
  const [inviteEmail, setInviteEmail] = useState('');

  const availableSystemUsers = users.filter(systemUser => 
    !addedMembers.some(member => member.id === systemUser.id)
  );

  const filteredSystemUsers = availableSystemUsers.filter(user => 
    user.displayName.toLowerCase().includes(existingUserSearch.toLowerCase()) ||
    (user.emailAddress && user.emailAddress.toLowerCase().includes(existingUserSearch.toLowerCase()))
  );

  const handleAddExistingMember = async (user: ProjectUser) => {
    if (!createdProject?.id) return;
    try {
      setIsAddingMember(true);
      const result = await apiService.addProjectMember(createdProject.id, user.emailAddress);
      
      if (result.status === 'added') {
        toast.success(`${user.displayName} added to project`);
        setAddedMembers(prev => [...prev, {
          id: user.id,
          displayName: user.displayName,
          emailAddress: user.emailAddress,
          avatarUrl: user.avatarUrl,
          status: 'added'
        }]);
      } else if (result.status === 'invited') {
        toast.success(`Invitation sent to ${user.emailAddress}`);
        setAddedMembers(prev => [...prev, {
          id: user.id,
          displayName: user.displayName,
          emailAddress: user.emailAddress,
          avatarUrl: user.avatarUrl,
          status: 'invited'
        }]);
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to add member');
    } finally {
      setIsAddingMember(false);
    }
  };

  const handleInviteByEmail = async () => {
    if (!createdProject?.id) return;
    const targetEmail = inviteEmail.trim().toLowerCase();
    if (!targetEmail) {
      toast.error('Please enter an email address');
      return;
    }
    
    // Check if already in addedMembers
    if (addedMembers.some(m => m.emailAddress.toLowerCase() === targetEmail)) {
      toast.error('User is already added to this project roster');
      return;
    }

    try {
      setIsAddingMember(true);
      const result = await apiService.addProjectMember(createdProject.id, targetEmail);
      
      if (result.status === 'added' && result.member) {
        toast.success('Member added to project');
        setAddedMembers(prev => [...prev, {
          id: result.member.id,
          displayName: result.member.display_name,
          emailAddress: result.member.email_address,
          avatarUrl: result.member.avatar_url,
          status: 'added'
        }]);
      } else if (result.status === 'invited') {
        toast.success(`Invitation email sent to ${targetEmail}`);
        setAddedMembers(prev => [...prev, {
          id: result.invitation_id || Math.random().toString(),
          displayName: targetEmail,
          emailAddress: targetEmail,
          avatarUrl: undefined,
          status: 'invited'
        }]);
      } else {
        toast.info(result.message || 'Action completed');
      }
      setInviteEmail('');
    } catch (err: any) {
      toast.error(err.message || 'Failed to send invitation');
    } finally {
      setIsAddingMember(false);
    }
  };

  const projectTypes = [
    { value: 'software', label: 'Software Development', icon: Code, color: 'indigo' },
    { value: 'business', label: 'Business Project', icon: Briefcase, color: 'emerald' },
    { value: 'service-desk', label: 'Service Desk', icon: LifeBuoy, color: 'purple' }
  ];

  const avatarOptions = ['🚀', '💻', '📱', '🌐', '⚡', '🔧', '📊', '🎯', '🏆', '💡', '🔥', '⭐'];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-surface-900/60 backdrop-blur-xs flex items-center justify-center z-50 p-4 transition-all duration-300">
      {step === 1 ? (
        // Step 1: Create / Edit details
        <div className="bg-white rounded-2xl shadow-premium border border-surface-150 w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden animate-fade-in-up">
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-surface-150 bg-gradient-to-r from-brand-600/5 via-indigo-600/5 to-transparent">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-brand-50 flex items-center justify-center text-brand-600 border border-brand-100 flex-shrink-0">
                <FolderKanban className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <h2 className="text-base font-bold text-surface-900 truncate">
                  {project ? 'Edit Project Settings' : 'Create New Project'}
                </h2>
                <p className="text-[11px] text-surface-400 mt-0.5 truncate">
                  {project ? 'Modify configurations and metadata for this project workspace.' : 'Set up a new workspace board and start tracking issues.'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-surface-100 hover:text-surface-600 rounded-lg text-surface-400 transition-all duration-150 hover:rotate-90 flex-shrink-0"
              title="Close modal"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Form Body */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Project Name */}
              <div>
                <label className="block text-xs font-bold text-surface-700 uppercase tracking-wide mb-1.5 flex items-center gap-1">
                  <FolderKanban className="w-3.5 h-3.5 text-brand-600" />
                  Project Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full text-xs px-3 py-2 border border-surface-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 bg-white"
                  placeholder="Enter high level project title"
                />
                <p className="text-[10px] text-surface-400 mt-1">Clear name for dashboard listings and reports.</p>
              </div>

              {/* Project Key */}
              <div>
                <label className="block text-xs font-bold text-surface-700 uppercase tracking-wide mb-1.5 flex items-center gap-1">
                  <Key className="w-3.5 h-3.5 text-brand-600" />
                  Project Key *
                </label>
                <input
                  type="text"
                  required
                  value={formData.key}
                  onChange={(e) => setFormData({ ...formData, key: e.target.value.toUpperCase() })}
                  className="w-full text-xs px-3 py-2 border border-surface-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 bg-white font-mono uppercase"
                  placeholder="e.g., PROJ"
                  maxLength={10}
                />
                <p className="text-[10px] text-surface-400 mt-1">Short unique prefix for issue keys (e.g. PROJ-123).</p>
              </div>

              {/* Description */}
              <div className="md:col-span-2">
                <label className="block text-xs font-bold text-surface-700 uppercase tracking-wide mb-1.5 flex items-center gap-1">
                  <FileText className="w-3.5 h-3.5 text-brand-600" />
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  className="w-full text-xs px-3 py-2 border border-surface-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 bg-white resize-none"
                  placeholder="Summarize the core objectives, stack, or target audience for this project..."
                />
              </div>

              {/* Project Type */}
              <div className="md:col-span-2">
                <label className="block text-xs font-bold text-surface-700 uppercase tracking-wide mb-2.5">
                  Project Type *
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {projectTypes.map((type) => {
                    const Icon = type.icon;
                    const isSelected = formData.type === type.value;
                    return (
                      <label
                        key={type.value}
                        className={`flex items-start gap-3 p-3 border rounded-xl cursor-pointer transition-all duration-200 hover:shadow-xs ${
                          isSelected
                            ? 'border-brand-500 bg-brand-50/30 ring-1 ring-brand-500 scale-[1.01]'
                            : 'border-surface-200 bg-white hover:border-surface-300 hover:bg-surface-50/20'
                        }`}
                      >
                        <input
                          type="radio"
                          name="type"
                          value={type.value}
                          checked={isSelected}
                          onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                          className="sr-only"
                        />
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                          isSelected ? 'bg-brand-600 text-white' : 'bg-surface-100 text-surface-500'
                        }`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="text-[11px] font-semibold text-surface-850 block">{type.label}</span>
                          <span className="text-[9px] text-surface-400 block mt-0.5 leading-normal">
                            {type.value === 'software' && 'Agile boards, issue tracking, and software dev.'}
                            {type.value === 'business' && 'Task tracking lists and team collaboration.'}
                            {type.value === 'service-desk' && 'Customer support desk and queues.'}
                          </span>
                        </div>
                        {isSelected && (
                          <div className="w-3.5 h-3.5 rounded-full bg-brand-600 text-white flex items-center justify-center flex-shrink-0 self-center">
                            <Check className="w-2.5 h-2.5" />
                          </div>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Project Lead */}
              <div className="relative" ref={dropdownRef}>
                <label className="block text-xs font-bold text-surface-700 uppercase tracking-wide mb-1.5 flex items-center gap-1">
                  <User className="w-3.5 h-3.5 text-brand-600" />
                  Project Lead *
                </label>
                
                <button
                  type="button"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="w-full text-xs px-3 py-2 border border-surface-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 bg-white flex items-center justify-between text-left hover:border-surface-300 transition-colors"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {selectedLead?.avatarUrl ? (
                      <img 
                        src={selectedLead.avatarUrl} 
                        alt={selectedLead.displayName} 
                        className="w-5 h-5 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-5 h-5 rounded-full bg-brand-100 text-brand-600 flex items-center justify-center font-bold text-[10px]">
                        {selectedLead?.displayName?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                    )}
                    <span className="truncate text-surface-850 font-medium">
                      {selectedLead ? selectedLead.displayName : 'Select a Project Lead'}
                    </span>
                    {selectedLead?.emailAddress && (
                      <span className="text-[10px] text-surface-400 truncate hidden sm:inline">
                        ({selectedLead.emailAddress})
                      </span>
                    )}
                  </div>
                  <Users className="w-3.5 h-3.5 text-surface-400 flex-shrink-0 ml-2" />
                </button>

                {isDropdownOpen && (
                  <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-surface-200 rounded-xl shadow-premium overflow-hidden flex flex-col max-h-60">
                    <div className="p-2 border-b border-surface-100 bg-surface-25/50 flex items-center gap-1.5">
                      <Search className="w-3.5 h-3.5 text-surface-400 flex-shrink-0" />
                      <input
                        type="text"
                        value={leadSearch}
                        onChange={(e) => setLeadSearch(e.target.value)}
                        placeholder="Search by name or email..."
                        className="w-full text-xs bg-transparent border-none focus:outline-none focus:ring-0 placeholder-surface-350 p-0.5"
                        autoFocus
                      />
                      {leadSearch && (
                        <button 
                          type="button"
                          onClick={() => setLeadSearch('')}
                          className="text-[10px] text-surface-400 hover:text-surface-600 p-0.5"
                        >
                          Clear
                        </button>
                      )}
                    </div>

                    <div className="overflow-y-auto flex-1 py-1 divide-y divide-surface-50">
                      {filteredUsers.length === 0 ? (
                        <p className="text-[10px] text-surface-400 text-center py-4">No members found</p>
                      ) : (
                        filteredUsers.map((user) => {
                          const isChosen = formData.lead === user.id;
                          return (
                            <button
                              key={user.id}
                              type="button"
                              onClick={() => {
                                setFormData({ ...formData, lead: user.id });
                                setIsDropdownOpen(false);
                                setLeadSearch('');
                              }}
                              className={`w-full text-left px-3 py-2 text-xs flex items-center justify-between hover:bg-surface-50 transition-colors ${
                                isChosen ? 'bg-brand-50/50 text-brand-700 font-medium' : 'text-surface-700'
                              }`}
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                {user.avatarUrl ? (
                                  <img 
                                    src={user.avatarUrl} 
                                    alt={user.displayName} 
                                    className="w-5 h-5 rounded-full object-cover"
                                  />
                                ) : (
                                  <div className="w-5 h-5 rounded-full bg-brand-100 text-brand-600 flex items-center justify-center font-bold text-[10px]">
                                    {user.displayName?.charAt(0)?.toUpperCase() || 'U'}
                                  </div>
                                )}
                                <div className="min-w-0">
                                  <span className="block truncate">{user.displayName}</span>
                                  <span className="block text-[9px] text-surface-400 truncate">{user.emailAddress}</span>
                                </div>
                              </div>
                              {isChosen && (
                                <Check className="w-3.5 h-3.5 text-brand-600 flex-shrink-0 ml-2" />
                              )}
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Category */}
              <div>
                <label className="block text-xs font-bold text-surface-700 uppercase tracking-wide mb-1.5 flex items-center gap-1">
                  <Tag className="w-3.5 h-3.5 text-brand-600" />
                  Category
                </label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full text-xs px-3 py-2 border border-surface-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 bg-white"
                  placeholder="e.g., Development, Marketing"
                />
              </div>

              {/* Project URL */}
              <div className="md:col-span-2">
                <label className="block text-xs font-bold text-surface-700 uppercase tracking-wide mb-1.5 flex items-center gap-1">
                  <Link className="w-3.5 h-3.5 text-brand-600" />
                  Project URL
                </label>
                <input
                  type="url"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  className="w-full text-xs px-3 py-2 border border-surface-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 bg-white"
                  placeholder="https://example.com/project-docs"
                />
              </div>

              {/* Timeline */}
              <div className="bg-surface-25/50 p-4 rounded-xl border border-surface-100 md:col-span-2">
                <span className="block text-xs font-bold text-surface-700 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5 text-brand-600" />
                  Project Timeline Schedule
                </span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
                  <div>
                    <label className="block text-[10px] font-semibold text-surface-450 mb-1">
                      Start Date
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        value={formData.startDate}
                        onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                        className="w-full text-xs px-3 py-2 pl-8 border border-surface-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 bg-white"
                      />
                      <Calendar className="w-3.5 h-3.5 text-surface-400 absolute left-2.5 top-2.5 pointer-events-none" />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-[10px] font-semibold text-surface-450 mb-1">
                      Target End Date
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        value={formData.endDate}
                        onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                        className="w-full text-xs px-3 py-2 pl-8 border border-surface-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 bg-white"
                      />
                      <Calendar className="w-3.5 h-3.5 text-surface-400 absolute left-2.5 top-2.5 pointer-events-none" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Avatar picker tray */}
              <div className="md:col-span-2 bg-surface-25/50 p-4 rounded-xl border border-surface-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-xl bg-brand-50 flex items-center justify-center text-2xl shadow-sm border border-brand-100 flex-shrink-0 animate-pulse">
                    {formData.avatar}
                  </div>
                  <div className="min-w-0">
                    <label className="block text-xs font-bold text-surface-700 uppercase tracking-wide truncate">
                      Project Avatar / Icon
                    </label>
                    <p className="text-[10px] text-surface-400 mt-0.5 leading-relaxed truncate">Select a representative symbol for your workspace identifier key.</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {avatarOptions.map((avatar) => (
                    <button
                      key={avatar}
                      type="button"
                      onClick={() => setFormData({ ...formData, avatar })}
                      className={`w-9 h-9 rounded-lg flex items-center justify-center text-lg transition-all duration-200 hover:scale-110 ${
                        formData.avatar === avatar
                          ? 'bg-brand-50 border-2 border-brand-500 shadow-soft scale-105'
                          : 'bg-white hover:bg-surface-50 border border-surface-200'
                      }`}
                    >
                      {avatar}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer buttons */}
            <div className="flex items-center justify-end gap-3 pt-5 border-t border-surface-150">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-xs font-semibold text-surface-600 hover:bg-surface-100 rounded-lg transition-all duration-150"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="px-5 py-2 text-xs font-semibold text-white bg-brand-600 hover:bg-brand-700 hover:shadow-md active:scale-95 rounded-lg transition-all duration-150 disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : (project ? 'Save Configuration' : 'Create Workspace')}
              </button>
            </div>
          </form>
        </div>
      ) : (
        // Step 2: Invite team members (only for new projects)
        <div className="bg-white rounded-2xl shadow-premium border border-surface-150 w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden animate-fade-in-up">
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-surface-150 bg-gradient-to-r from-brand-600/5 via-indigo-600/5 to-transparent">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-brand-50 flex items-center justify-center text-brand-600 border border-brand-100 flex-shrink-0">
                <Users className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <h2 className="text-base font-bold text-surface-900 truncate">
                  Invite Team Members
                </h2>
                <p className="text-[11px] text-surface-400 mt-0.5 truncate leading-relaxed">
                  Add users to your new project board "{createdProject?.name}" to start collaborating.
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-surface-100 hover:text-surface-600 rounded-lg text-surface-400 transition-all duration-150 flex-shrink-0"
              title="Close modal"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left side: Add/Invite Inputs */}
              <div className="space-y-5">
                {/* Invite by Email */}
                <div className="bg-surface-25/50 p-4 rounded-xl border border-surface-100">
                  <label className="block text-xs font-bold text-surface-700 uppercase tracking-wide mb-2">
                    Invite by Email
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="teammate@company.com"
                      className="flex-1 text-xs px-3 py-2 border border-surface-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 bg-white"
                    />
                    <button
                      type="button"
                      onClick={handleInviteByEmail}
                      disabled={isAddingMember}
                      className="px-4 py-2 text-xs font-semibold text-white bg-brand-600 hover:bg-brand-700 active:scale-95 rounded-lg transition-all duration-150 disabled:opacity-50"
                    >
                      {isAddingMember ? 'Inviting...' : 'Send Invite'}
                    </button>
                  </div>
                  <p className="text-[10px] text-surface-450 mt-1.5 leading-relaxed">New users will receive an email invitation to create an account.</p>
                </div>

                {/* Choose from Existing Users */}
                <div className="bg-surface-25/50 p-4 rounded-xl border border-surface-100 flex flex-col h-[280px]">
                  <label className="block text-xs font-bold text-surface-700 uppercase tracking-wide mb-2 flex items-center justify-between">
                    <span>Add Existing Team Members</span>
                    <span className="text-[10px] text-surface-400 lowercase font-normal">
                      {availableSystemUsers.length} available
                    </span>
                  </label>
                  
                  {/* Search existing users */}
                  <div className="relative mb-3">
                    <Search className="w-3.5 h-3.5 text-surface-400 absolute left-2.5 top-2.5" />
                    <input
                      type="text"
                      value={existingUserSearch}
                      onChange={(e) => setExistingUserSearch(e.target.value)}
                      placeholder="Search users by name or email..."
                      className="w-full text-xs px-3 py-1.5 pl-8 border border-surface-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 bg-white"
                    />
                  </div>

                  {/* Users List */}
                  <div className="flex-1 overflow-y-auto divide-y divide-surface-100 border border-surface-150 rounded-lg bg-white">
                    {filteredSystemUsers.length === 0 ? (
                      <p className="text-[10px] text-surface-400 text-center py-8">No users found to add</p>
                    ) : (
                      filteredSystemUsers.map((user) => (
                        <div key={user.id} className="p-2.5 flex items-center justify-between hover:bg-surface-50 transition-colors">
                          <div className="flex items-center gap-2 min-w-0">
                            {user.avatarUrl ? (
                              <img src={user.avatarUrl} alt={user.displayName} className="w-6 h-6 rounded-full object-cover" />
                            ) : (
                              <div className="w-6 h-6 rounded-full bg-brand-100 text-brand-600 flex items-center justify-center font-bold text-[10px]">
                                {user.displayName.charAt(0).toUpperCase()}
                              </div>
                            )}
                            <div className="min-w-0">
                              <span className="text-xs font-semibold text-surface-800 block truncate">{user.displayName}</span>
                              <span className="text-[9px] text-surface-400 block truncate">{user.emailAddress}</span>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleAddExistingMember(user)}
                            disabled={isAddingMember}
                            className="px-2.5 py-1 text-[10px] font-bold text-brand-600 hover:text-brand-700 bg-brand-50 hover:bg-brand-100 rounded transition-colors disabled:opacity-50"
                          >
                            Add to Project
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* Right side: Project Roster / Current Members */}
              <div className="bg-surface-25/50 p-4 rounded-xl border border-surface-100 flex flex-col h-[395px]">
                <label className="block text-xs font-bold text-surface-700 uppercase tracking-wide mb-3">
                  Project Roster ({addedMembers.length})
                </label>

                <div className="flex-1 overflow-y-auto divide-y divide-surface-100 border border-surface-150 rounded-lg bg-white">
                  {addedMembers.map((member) => {
                    const isLead = member.id === formData.lead;
                    return (
                      <div key={member.id} className="p-2.5 flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          {member.avatarUrl ? (
                            <img src={member.avatarUrl} alt={member.displayName} className="w-6 h-6 rounded-full object-cover" />
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-brand-100 text-brand-600 flex items-center justify-center font-bold text-[10px]">
                              {member.displayName.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div className="min-w-0">
                            <span className="text-xs font-semibold text-surface-800 block truncate">{member.displayName}</span>
                            <span className="text-[9px] text-surface-400 block truncate">{member.emailAddress}</span>
                          </div>
                        </div>
                        <div>
                          {isLead ? (
                            <span className="text-[9px] font-bold text-yellow-600 bg-yellow-50 px-1.5 py-0.5 rounded border border-yellow-100 uppercase">
                              Owner / Lead
                            </span>
                          ) : (
                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border uppercase ${
                              member.status === 'invited' 
                                ? 'text-orange-600 bg-orange-50 border-orange-100' 
                                : 'text-green-600 bg-green-50 border-green-100'
                            }`}>
                              {member.status || 'Member'}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-5 bg-surface-25 border-t border-surface-150">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 text-xs font-bold text-white bg-brand-600 hover:bg-brand-700 hover:shadow-md active:scale-95 rounded-lg transition-all duration-150"
            >
              Finish & Launch Workspace
            </button>
          </div>
        </div>
      )}
    </div>
  );
};